import React, {
  createContext,
  FC,
  useContext,
  useState,
  ReactNode,
} from 'react';
import { useImmerState } from '../hooks/useImmerState';
import { IChanges } from '../models';

interface ContextProps<T> {
  value: T;
  setValue: React.Dispatch<React.SetStateAction<T>>;
  extraOptions: { [key: string]: any };
  setExtraOptions: React.Dispatch<React.SetStateAction<any>>;
  onChange?: (value: IChanges) => any;
  setOnChange: (value: (val: IChanges) => any) => void;
}

const createGenericContext = <T extends unknown>() => {
  const Context = createContext<ContextProps<T> | undefined>(undefined);

  const useContextValue = (returnOnlyValue = false) => {
    const context = useContext(Context);
    if (!context) {
      throw new Error('useContextValue must be used within a Provider');
    }
    return !returnOnlyValue ? context : context.value;
  };

  const Provider: FC<{ initialValue: T; children: ReactNode }> = ({
    initialValue,
    children,
  }) => {
    const [value, setValue] = useImmerState<T>(initialValue) as [
      T,
      React.Dispatch<React.SetStateAction<T>>
    ]; // Type assertion
    const [extraOptions, setExtraOptions] = useImmerState({});
    const [onChange, setOnChange] = useState<
      ((value: IChanges) => any) | undefined
    >();

    return (
      <Context.Provider
        value={{
          value,
          setValue,
          extraOptions,
          setExtraOptions,
          onChange,
          setOnChange,
        }}
      >
        {children}
      </Context.Provider>
    );
  };

  return { Provider, useContextValue };
};

export default createGenericContext;
